import {
    Link,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import React, { useEffect, useState } from "react";
import { SignInFactory } from "../pages/signin/signin-factory";
import { FooterLayout } from "./footer-layout";
import { useRouteRole } from "../hooks/use-route-role";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { Dropdown, Spin } from "antd";
import { ResetPassword } from "../pages/reset-password/reset-password";
import { ForgotPassword } from "../pages/forgot-password/forgot-password";
import ChartPreview from "../pages/_preview/chart-preview";
import { ThemeToggle } from "../components/theme-toggle-component/theme-toggle-component";
import { LanguageSwitcher } from "../components/language-switcher/language-switcher";
import { Bell, LogOut, UserPen } from "lucide-react";
import { MeContext } from "../contexts/me-context/me-context";
import { ProfileEditModal } from "../components/modals/profile-edit-modal";
import { createAction } from "../services/create-action";
import { useTranslation } from "react-i18next";
import { UserModel } from "../types/user";
import { configProject } from "../config-project";

import {
    SidebarProvider,
    Sidebar,
    SidebarHeader,
    SidebarContent,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarFooter,
    SidebarInset,
    SidebarRail,
    SidebarTrigger,
    useSidebar,
} from "../components/ui/sidebar";

interface RouteItem {
    id: string;
    path: string;
    icon: React.ReactNode;
    name: string;
    group: string;
}

interface MainLayoutProps {
    me: UserModel | null;
    role: { id: string | null };
}

/** Sidebar interna — deve ficar dentro do SidebarProvider para acessar useSidebar() */
const SidebarNav: React.FC<{ menuRoutes: RouteItem[] }> = ({ menuRoutes }) => {
    const { state } = useSidebar();
    const collapsed = state === "collapsed";
    const location = useLocation();

    // Agrupa itens contíguos por `group` (o array já vem ordenado por seção).
    const sections = React.useMemo(() => {
        const out: { label: string; items: RouteItem[] }[] = [];
        menuRoutes.forEach((route) => {
            const last = out[out.length - 1];
            if (last && last.label === route.group) last.items.push(route);
            else out.push({ label: route.group, items: [route] });
        });
        return out;
    }, [menuRoutes]);

    return (
        <Sidebar collapsible="icon">
            <SidebarHeader
                className="flex items-center justify-center border-b border-sidebar-border overflow-hidden"
                style={{
                    minHeight: collapsed ? 56 : 100,
                    transition: "min-height 200ms ease",
                }}
            >
                <img
                    src={require("../assets/dream-book-logo.png")}
                    alt="Logo"
                    style={{
                        maxHeight: collapsed ? 30 : 100,
                        width: "auto",
                        transition: "max-height 200ms ease",
                        objectFit: "contain",
                    }}
                />
            </SidebarHeader>
            <SidebarContent className="custom-scrollbar gap-0 py-2">
                {sections.map((section, i) => (
                    <SidebarGroup
                        key={section.label}
                        className={`px-2 pb-1 ${
                            i > 0
                                ? "mt-3 border-t border-sidebar-border/60 pt-4"
                                : "pt-1"
                        }`}
                    >
                        <SidebarGroupLabel className="mb-1 h-5 px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/55">
                            {section.label}
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="gap-0.5">
                                {section.items.map((route) => {
                                    const active =
                                        location.pathname.startsWith(route.path);
                                    return (
                                        <SidebarMenuItem
                                            key={route.id}
                                            className="px-2"
                                        >
                                            <SidebarMenuButton
                                                asChild
                                                isActive={active}
                                                className="h-9 text-sm text-sidebar-foreground/75 [&>svg]:text-primary data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:shadow-sm data-[active=true]:hover:text-sidebar-primary-foreground data-[active=true]:[&>svg]:text-sidebar-primary-foreground"
                                                style={
                                                    active
                                                        ? {
                                                              backgroundImage:
                                                                  configProject.colorBgHeaderGradient,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <Link to={route.path}>
                                                    {route.icon}
                                                    <span>{route.name}</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                ))}
            </SidebarContent>
            <SidebarFooter className="p-0">
                <FooterLayout />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
};

export const MainLayout = ({ me, role }: MainLayoutProps) => {
    const { t } = useTranslation("sidebar");
    const [logged, setLogged] = useState(false);
    const { setMe } = React.useContext(MeContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const { routes } = useRouteRole();
    const [isMounted, setIsMounted] = React.useState(false);
    const [loading, setLoading] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const isChatPage = location.pathname.includes("/chat");

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // O menu da conta nunca deve nascer aberto ao logar/navegar (evita o dropdown
    // abrindo sozinho na transição de mount pós-login).
    useEffect(() => {
        setAccountOpen(false);
    }, [me?.id, location.pathname]);

    useEffect(() => {
        if (isMounted) {
            if (
                !me?.id &&
                location.pathname !== "/signin" &&
                location.pathname !== "/reset-password" &&
                location.pathname !== "/preview-charts"
            ) {
                navigate("/signin");
            }
            if (me?.id && location.pathname === "/signin") {
                navigate("/dashboard");
            }
        }
    }, [me, isMounted]);

    // TEMPORÁRIO — preview de charts acessível logado ou não.
    if (location.pathname === "/preview-charts") {
        return <ChartPreview />;
    }

    const getUserInitials = () => {
        if (!me?.name) return "U";

        const nameParts = me.name.split(" ");

        if (nameParts.length === 1) {
            return nameParts[0][0].toUpperCase();
        }

        return (
            nameParts[0][0] + nameParts[nameParts.length - 1][0]
        ).toUpperCase();
    };

    const resolveAvatarSrc = (avatar: unknown): string | null => {
        if (!avatar) return null;
        if (typeof avatar === "string") return avatar;
        if (typeof avatar === "object") {
            const obj = avatar as { url?: string; path?: string };
            return obj.url || obj.path || null;
        }
        return null;
    };

    const avatarSrc = resolveAvatarSrc(me?.avatar);

    const handleAvatarUpdated = (updated: UserModel) => {
        setMe(updated);
        localStorage.setItem("user", JSON.stringify(updated));
    };

    const handleLogout = async () => {
        setLogged(false);

        // Log the activity while the token is still present so the backend can attribute it.
        await createAction({ action: "USER_SIGN_OUT", name: me?.name ?? "" });

        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (setMe) {
            setMe(null);
        }

        navigate("/signin");
    };

    const accountDropdownRender = () => (
        <div className="w-64 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-lg">
            <div className="flex items-center gap-3 px-2.5 py-2.5">
                <div className="shrink-0 rounded-full bg-gradient-to-br from-[#9b87f5] via-[#7c3aed] to-[#4f46e5] p-[2px]">
                    <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-semibold text-primary-foreground ring-2 ring-card">
                        {avatarSrc ? (
                            <img
                                src={avatarSrc}
                                alt={me?.name}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            getUserInitials()
                        )}
                    </div>
                </div>
                <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-semibold leading-tight text-card-foreground">
                        {me?.name || "—"}
                    </span>
                    {me?.email && (
                        <span className="truncate text-xs text-muted-foreground">
                            {me.email}
                        </span>
                    )}
                    {me?.role && (
                        <span className="mt-1.5 inline-flex w-fit items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                            {me.role}
                        </span>
                    )}
                </div>
            </div>

            <div className="mx-1 my-1.5 h-px bg-border" />

            <button
                type="button"
                onClick={() => {
                    setAccountOpen(false);
                    setProfileOpen(true);
                }}
                className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-card-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            >
                <UserPen
                    size={16}
                    className="text-muted-foreground transition-colors group-hover:text-foreground"
                />
                {t("layout.editProfile")}
            </button>
            <button
                type="button"
                onClick={handleLogout}
                className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 focus-visible:bg-destructive/10 focus-visible:outline-none"
            >
                <LogOut size={16} />
                {t("layout.logout")}
            </button>
        </div>
    );

    if (!me?.id) {
        return (
            <Routes>
                <Route path={"/preview-charts"} element={<ChartPreview />} />
                <Route path={"/signin"} element={<SignInFactory />} />
                <Route
                    path={"/signup"}
                    element={<Link to={"/signin"}>signup</Link>}
                />
                <Route path={"/forgot-password"} element={<ForgotPassword />} />
                <Route
                    path={"/reset-password"}
                    element={<ResetPassword />}
                />
            </Routes>
        );
    }

    return (
        <Spin spinning={loading}>
            <SidebarProvider>
                <SidebarNav menuRoutes={routes.location} />

                <SidebarInset>
                    <header
                        className="flex h-[73px] shrink-0 items-center gap-2 border-b border-white/10 px-6 justify-between shadow-sm"
                        style={{
                            backgroundColor: configProject.colorBgHeader,
                            backgroundImage:
                                configProject.colorBgHeaderGradient,
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <SidebarTrigger className="text-white hover:bg-white/10 hover:text-white" />
                            <div className="w-px h-6 bg-white/30 rounded-full hidden sm:block" />
                            <span className="hidden sm:block text-[11px] font-semibold tracking-widest uppercase text-white/70 leading-none">
                                {t("layout.controlPanel")}
                            </span>
                        </div>

                        <div className="flex items-center gap-4">
                            <LanguageSwitcher variant="dark" />
                            <ThemeToggle className="text-white hover:bg-white/10 hover:text-white" />
                            {/*
                              O sino é a caixa de entrada de QUEM ESTÁ USANDO o painel (ex.: avisos de
                              atividade para a equipe). Campanhas e tipos de notificação para os
                              usuários do app ficam no menu lateral, em "Notificações".
                            */}
                            <Dropdown
                                trigger={["click"]}
                                placement="bottomRight"
                                arrow
                                dropdownRender={() => (
                                    <div className="w-72 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground shadow-lg">
                                        {t("layout.noNotifications")}
                                    </div>
                                )}
                            >
                                <button
                                    type="button"
                                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                                    title={t("layout.notifications")}
                                    aria-label={t("layout.viewNotifications")}
                                >
                                    <Bell size={20} className="text-white" />
                                </button>
                            </Dropdown>
                            <Dropdown
                                open={accountOpen}
                                onOpenChange={setAccountOpen}
                                dropdownRender={accountDropdownRender}
                                placement="bottomRight"
                                arrow
                                trigger={["click"]}
                            >
                                <button
                                    type="button"
                                    aria-label={t("layout.account")}
                                    title={t("layout.account")}
                                    className="w-10 h-10 overflow-hidden rounded-full bg-white/20 text-white flex items-center justify-center font-medium hover:bg-white/30 cursor-pointer border border-white/30"
                                >
                                    {avatarSrc ? (
                                        <img
                                            src={avatarSrc}
                                            alt={me?.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        getUserInitials()
                                    )}
                                </button>
                            </Dropdown>
                        </div>
                    </header>
                    <div className="flex flex-1 flex-col bg-background">
                        <div className={`h-full ${isChatPage ? "" : "p-6"}`}>
                            {routes.routes}
                        </div>
                    </div>
                </SidebarInset>
            </SidebarProvider>

            <ProfileEditModal
                open={profileOpen}
                onOpenChange={setProfileOpen}
                me={me}
                currentAvatarSrc={avatarSrc}
                initials={getUserInitials()}
                onUpdated={handleAvatarUpdated}
            />
        </Spin>
    );
};
